import axios from "axios";

// Custom reports 2.0 (owner, 2026-10-24, v2.1): built in Settings, run from Reports.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/custom-reports`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

const range = (fromDate, toDate) => ({
  ...(fromDate ? { from_date: fromDate } : {}),
  ...(toDate ? { to_date: toDate } : {}),
});

export const getCatalogue = token => axios.get(`${API}/catalogue`, auth(token)).then(r => r.data);

export const listCustomReports = token => axios.get(API, auth(token)).then(r => r.data);

export const createCustomReport = (name, definition, token, isTemplate = false) =>
  axios.post(API, { name, definition, is_template: isTemplate }, auth(token)).then(r => r.data);

export const updateCustomReport = (id, name, definition, token, isTemplate = false) =>
  axios.put(`${API}/${id}`, { name, definition, is_template: isTemplate }, auth(token)).then(r => r.data);

export const duplicateCustomReport = (id, newName, token) =>
  axios.post(`${API}/${id}/duplicate`, { new_name: newName }, auth(token)).then(r => r.data);

export const deleteCustomReport = (id, token) => axios.delete(`${API}/${id}`, auth(token));

export const previewCustomReport = (definition, { fromDate, toDate } = {}, token) =>
  axios.post(`${API}/preview`, { definition, ...range(fromDate, toDate) }, auth(token)).then(r => r.data);

export const runCustomReport = (id, { fromDate, toDate } = {}, token) =>
  axios.get(`${API}/${id}/run`, { ...auth(token), params: range(fromDate, toDate) }).then(r => r.data);

// ---------------------------------------------------------------------------
// Shared Classification Profiles API
// ---------------------------------------------------------------------------

export const listClassificationProfiles = (token, entity = "order_items") =>
  axios.get(`${API}/classifications`, { ...auth(token), params: { entity } }).then(r => r.data);

export const getClassificationProfile = (slug, version = null, token) =>
  axios.get(`${API}/classifications/${slug}`, {
    ...auth(token),
    params: version ? { version } : {},
  }).then(r => r.data);

export const saveClassificationProfile = (profileData, token) =>
  axios.post(`${API}/classifications`, profileData, auth(token)).then(r => r.data);

export const duplicateClassificationProfile = (slug, newSlug, newName, token) =>
  axios.post(`${API}/classifications/${slug}/duplicate`, { new_slug: newSlug, new_name: newName }, auth(token)).then(r => r.data);

export const simulateClassification = (rules, defaultOutput = "Other", token) =>
  axios.post(`${API}/classifications/simulate`, { rules, default_output: defaultOutput }, auth(token)).then(r => r.data);

// ---------------------------------------------------------------------------
// Drilldown & Export API
// ---------------------------------------------------------------------------

export const drilldownReport = (id, bucketValue, { fromDate, toDate, limit = 100 } = {}, token) =>
  axios.get(`${API}/${id}/drilldown`, {
    ...auth(token),
    params: { bucket_value: bucketValue, limit, ...range(fromDate, toDate) },
  }).then(r => r.data);

export const exportCustomReportBlob = (id, format = "xlsx", { fromDate, toDate } = {}, token) =>
  axios.get(`${API}/${id}/export`, {
    ...auth(token),
    params: { format, ...range(fromDate, toDate) },
    responseType: "blob",
  });

export const exportPreviewBlob = (definition, format = "xlsx", { fromDate, toDate } = {}, token) =>
  axios.post(
    `${API}/preview/export`,
    { definition, format, ...range(fromDate, toDate) },
    { ...auth(token), responseType: "blob" }
  );

/**
 * Trigger browser file download from Blob response.
 */
export const downloadBlobFile = (response, fallbackFilename = "report.xlsx") => {
  let filename = fallbackFilename;
  const disposition = response.headers["content-disposition"];
  if (disposition && disposition.indexOf("filename=") !== -1) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * FastAPI answers a 422 with a list of dicts; React throws on an object child.
 * Everything shown from a failure is a string, naming what went wrong.
 */
export const failureText = (failure, fallback) => {
  const detail = failure?.response?.data?.detail;
  const one = item => (typeof item === "string" ? item : item?.msg || JSON.stringify(item));
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map(one).join("; ");
  if (detail && typeof detail === "object") return one(detail);
  return failure?.message || fallback;
};
